<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'cancel']);

const store = useStore();

// The selected subject id; ComboBox emits '' when nothing is selected.
const selectedSubject = ref('');
const summary = ref('');

const subjects = useMapGetter(
  'conversationResolutionSubject/getConversationResolutionSubjects'
);
const uiFlags = useMapGetter('conversationResolutionSubject/getUIFlags');

const rules = computed(() => ({
  selectedSubject: { required },
}));

const v$ = useVuelidate(rules, { selectedSubject });

const hasSubjects = computed(() => subjects.value.length > 0);

const subjectOptions = computed(() =>
  subjects.value.map(({ id, name }) => ({ value: id, label: name }))
);

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasSubjects.value
);

const submitState = reactive({ showLoading: false });

const fetchSubjects = async () => {
  try {
    await store.dispatch(
      'conversationResolutionSubject/getConversationResolutionSubject'
    );
  } catch (error) {
    // ignore
  }
};

onMounted(() => {
  if (!subjects.value.length) fetchSubjects();
});

const handleConfirm = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  emit('confirm', {
    conversationResolutionSubjectId: selectedSubject.value,
    summary: summary.value || null,
  });
};

const handleCancel = () => emit('cancel');
</script>

<template>
  <woot-modal :show="show" :on-close="handleCancel">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.TITLE')"
      />

      <div v-if="uiFlags.fetchingList" class="p-4 text-sm text-slate-500">
        {{ $t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.LOADING') }}
      </div>

      <form
        v-else
        class="flex flex-col w-full"
        data-testid="resolution-form"
        @submit.prevent="handleConfirm"
      >
        <div v-if="!hasSubjects" class="p-4 text-sm text-red-500">
          {{
            $t(
              'CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.EMPTY_SUBJECTS_HINT'
            )
          }}
        </div>

        <template v-else>
          <div class="w-full">
            <label :class="{ 'text-red-500': v$.selectedSubject.$error }">
              {{
                $t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.SUBJECT_LABEL')
              }}
            </label>
            <ComboBox
              v-model="selectedSubject"
              :options="subjectOptions"
              :placeholder="
                $t(
                  'CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.SUBJECT_PLACEHOLDER'
                )
              "
              :empty-state="
                $t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.NO_RESULTS')
              "
              :has-error="v$.selectedSubject.$error"
              data-testid="subject-select"
              @update:model-value="v$.selectedSubject.$touch"
            />
            <span v-if="v$.selectedSubject.$error" class="text-red-500 text-xs">
              {{
                $t(
                  'CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.SUBJECT_REQUIRED'
                )
              }}
            </span>
          </div>

          <div class="w-full">
            <label>
              {{
                $t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.SUMMARY_LABEL')
              }}
              <textarea
                v-model="summary"
                data-testid="summary-input"
                :rows="3"
                class="summary-textarea"
                :placeholder="
                  $t(
                    'CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.SUMMARY_PLACEHOLDER'
                  )
                "
              />
            </label>
          </div>
        </template>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            type="submit"
            variant="solid"
            color="blue"
            :label="$t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.CONFIRM')"
            :disabled="isSubmitDisabled || isLoading"
            :is-loading="submitState.showLoading || isLoading"
            data-testid="confirm-resolution"
          />
          <button
            class="button clear"
            data-testid="cancel-resolution"
            @click.prevent="handleCancel"
          >
            {{ $t('CONVERSATION.CONVERSATION_RESOLUTION_DIALOG.CANCEL') }}
          </button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<style scoped>
/* Override the global `textarea { height: 4rem }` base style — the scoped
   selector has higher specificity, so no !important is needed. */
.summary-textarea {
  height: 6rem;
  resize: vertical;
}
</style>
